import React, { useEffect, useState } from "react";
import { isEmpty } from "lodash";
import { useDispatch } from "react-redux";
import mixpanel from "mixpanel-browser";
import { Redirect } from "react-router-dom/cjs/react-router-dom.min";

import { actions as modelActions } from "features/Model/_ducks";
import { actions as resultsActions } from "features/Model/Review/Results/_ducks";
import { change } from "store/root/actions";
import { fetchDrawingData } from "features/Model/DrawingNew/_ducks/thunk";
import { getImageGallery } from "store/imageGallery/thunk";
import { saveModel } from "features/Model/_ducks/thunk";
import { unlockRxRResults } from "features/Model/_ducks/thunk";

import { useStateSelector } from "utils/field";
import { useLocalStorage } from "utils/hooks";
import { getTrialText, hasAccessToInsightsTab, hasInsightsAccess } from "utils/insights";

import Loading from "components/Loading";
import VerticalTabs from "components/Tabs/VerticalTabs";
import Button from "components/Button";
import FixedBanner from "components/FixedBanner";

import DrawingNew from "features/Model/DrawingNew";
import ImageGallery from "features/Model/ImageGallery";
import Settings from "features/Model/Settings";
import VersionControlDrawer from "./VersionControlDrawer";

import ActivateInsightsModal from "./ActivateInsightsModal";
import RoomByRoom from "./RoomByRoom";
import EnergyPlus from "./EnergyPlus";
import UtilityAnalysis from "./UtilityAnalysis";
import HybridHeating from "./HybridHeating";
import SingleZoneCooling from "./SingleZoneCooling";

import { useInsights } from "./InsightsContext";
import { useLayout, DEFAULT_INSIGHTS_TAB } from "hoc/Layout/LayoutProvider";

import classes from "./styles.module.scss";

import InsightsRoomByRoom from "assets/images/icons/JSX/InsightsRoomByRoom";
import InsightsEnergyPlus from "assets/images/icons/JSX/InsightsEnergyPlus";
import InsightsUtilityAnalysis from "assets/images/icons/JSX/InsightsUtilityAnalysis";
import InsightsHybridHeating from "assets/images/icons/JSX/InsightsHybridHeating";
import InsightsSingleZoneCooling from "assets/images/icons/JSX/InsightsSingleZoneCooling"; // Update icon file later

const { fetchModelData, modelLoading, checkModelUpdates } = modelActions;
const { getResults } = resultsActions;

const INSIGHTS_TABS = [
    { name: "Room-by-Room", url: "/room-by-room", IconComp: InsightsRoomByRoom },
    { name: "EnergyPlus", url: "/energy-plus", IconComp: InsightsEnergyPlus },
    { name: "Utility Analysis", url: "/utility-analysis", IconComp: InsightsUtilityAnalysis },
    { name: "Hybrid Heating", url: "/hybrid-heating", IconComp: InsightsHybridHeating },
    { name: "Single Zone Cooling", url: "/single-zone-cooling", IconComp: InsightsSingleZoneCooling },
];

const Insights = ({ history }) => {
    const {
        drawingOpen,
        setDrawingOpen,
        settingsOpen,
        setSettingsOpen,
        versionHistoryOpen,
        setVersionHistoryOpen,
        submitting,
        setSubmitting,
        isActivateInsightsModalOpen,
        setIsActivateInsightsModalOpen,
    } = useInsights();

    const {
        insightsTabActive,
        setInsightsTabActive,
    } = useLayout();

    const dispatch = useDispatch();

    const [isInitialLoading, setInitialLoading] = useState(true);
    const [navCollapsed, setNavCollapsed] = useLocalStorage("insightsNavCollapsed", false);

    const { location: { pathname = "" } = {} } = history || {};

    const { uid, isUserVerified } = useStateSelector("user");
    const model = useStateSelector("model") || {};

    const { loading, modelData, modelId: loadedModelId, modelDetails = {}, modelNotFound = false } = model;

    const {
        name = "",
        isInsightsActive = false,
        isRxRResultsUnlocked = null,
        createdAt = "",
        lastCliRun: { cliId: cliIds = {} } = {},
    } = modelDetails;

    const hasAccess = hasInsightsAccess({ isInsightsActive, createdAt });

    const pathParts = pathname.match(/^\/insights\/([^/]+)(\/.*)?$/);
    const modelId = pathParts?.[1];
    const tabSuffix = pathParts?.[2];
    const defaultTabUrl = hasAccess
        ? INSIGHTS_TABS[0]?.url
        : INSIGHTS_TABS.find((tab) => hasAccessToInsightsTab(tab.url, modelDetails))?.url || "/";
    const tabActive = !tabSuffix || tabSuffix === "/" ? defaultTabUrl : tabSuffix;

    const isInsightsTabPath = (url) => INSIGHTS_TABS.some((tab) => tab.url === url);

    const activeInsightsTab = isInsightsTabPath(tabSuffix) && tabSuffix !== "/"
        ? tabSuffix
        : isInsightsTabPath(insightsTabActive)
          ? insightsTabActive
          : isInsightsTabPath(defaultTabUrl)
            ? defaultTabUrl
            : DEFAULT_INSIGHTS_TAB;

    useEffect(() => {
        if (!modelId || isInitialLoading || loading) return;

        if (isInsightsTabPath(tabSuffix) && tabSuffix !== "/") {
            if (insightsTabActive !== tabSuffix) {
                setInsightsTabActive(tabSuffix);
            }
            return;
        }

        const restoredTab = isInsightsTabPath(insightsTabActive) ? insightsTabActive : defaultTabUrl;

        if (restoredTab && restoredTab !== "/") {
            history.replace(`/insights/${modelId}${restoredTab}`);
        }
    }, [modelId, pathname, loading, isInitialLoading, defaultTabUrl, tabSuffix, insightsTabActive, setInsightsTabActive, history]);

    const fetchModel = async () => {
        await dispatch(modelLoading());
        await dispatch(fetchDrawingData(modelId));
        await dispatch(checkModelUpdates(modelId));
        await dispatch(fetchModelData(modelId));
        await dispatch(getImageGallery(modelId, uid));
    };

    useEffect(() => {
        if (modelId === "null") return;
        
        const needsFetch = modelId && uid && !loading && (isEmpty(modelData) || loadedModelId !== modelId);

        if (needsFetch) {
            fetchModel();
        }

        if (!loading) setInitialLoading(false);

        return () => {
            setInitialLoading(true);
        };
    }, [modelId, loading]);

    useEffect(() => {
        if (!modelId || isInitialLoading || loading) return;
        mixpanel.track("Insights Open", {
            ModelId: modelId,
            UserId: uid,
            Tab: tabActive,
            IsInsightsActive: hasAccess,
        });
    }, [modelId, isInitialLoading, loading]);

    const {
        packageResults: packageResultsData = {},
        loading: resultsLoading = false,
        error: resultsError = "",
    } = useStateSelector("results") || {};

    const { base: { metadata: { mode = "" } = {} } = {} } = packageResultsData;

    const hasResults = !!mode;

    // Fetch results if not yet loaded
    useEffect(() => {
        if (!resultsLoading && !hasResults && Object.keys(cliIds).length > 0 && resultsError !== "Results not found.") {
            const runIds = Object.values(cliIds).map(({ runId = "" }) => runId);
            dispatch(getResults({ cliIds: runIds, modelId }));
        }
    }, [cliIds, hasResults, resultsError, resultsLoading, modelId]);

    // Check if room-by-room results should be unlocked (one-time server-side settle)
    useEffect(() => {
        if (!modelId || isEmpty(modelDetails)) return;

        if (isRxRResultsUnlocked == null) {
            dispatch(unlockRxRResults(modelId));
        }
    }, [modelId, modelDetails, isRxRResultsUnlocked]);

    useEffect(() => {
        if (hasAccess || isInitialLoading || loading || !modelId) return;
        const path = tabSuffix && tabSuffix !== "/" ? tabSuffix : null;
        if (!path || path === defaultTabUrl) return;
        if (!hasAccessToInsightsTab(path, modelDetails)) {
            history.replace(`/insights/${modelId}${defaultTabUrl}`);
        }
    }, [hasAccess, isInitialLoading, loading, modelId, tabSuffix, defaultTabUrl, modelDetails, history]);

    const handleSave = async () => {
        return dispatch(saveModel(modelId, setSubmitting));
    };

    const handleChange = (...args) => dispatch(change(...args));

    const handleTabChange = (url) => {
        mixpanel.track("Insights Tab Change", {
            Tab: url,
            ModelId: modelId,
            UserId: uid,
        });
        history.push(`/insights/${modelId}${url}`);
        setInsightsTabActive(url);
    };

    if (modelId === "null" || (!loading && modelNotFound)) {
        return <Redirect to="/404-page-not-found" />;
    }

    if (isInitialLoading || loading) {
        return <Loading />;
    }

    if (!isUserVerified) {
        return <div></div>;
    }

    return (
        <>
            {!hasAccess && (
                <FixedBanner type="info">
                    <p>
                        <strong>Some features below are free during their trial periods.</strong> Insights is not
                        activated for this model.                         Activate Insights (1 credit) for permanent access to Room-by-Room, EnergyPlus, Utility
                        Analysis, and Hybrid Heating. Your first parametric analysis from this model will include
                        2,500 unlocked runs.
                    </p>
                    <Button onClick={() => setIsActivateInsightsModalOpen(true)}>Activate Insights</Button>
                </FixedBanner>
            )}
            <div className={classes.insightsContainer}>
                <VerticalTabs
                    className={classes.verticalTabsContainer}
                    tabNavClassName={`${classes.tabNav} ${navCollapsed ? classes.tabNavCollapsed : ""}`}
                    collapsed={navCollapsed}
                    onToggleCollapsed={() => setNavCollapsed(!navCollapsed)}
                    activeTabClassName={classes.activeTab}
                    tabLabelClassName={classes.tabLabel}
                    title="Insights"
                    tabActive={activeInsightsTab}
                    tabs={
                        hasAccess
                            ? INSIGHTS_TABS
                            : INSIGHTS_TABS.map((tab) => {
                                  const isDisabled = !hasAccessToInsightsTab(tab.url, modelDetails);

                                  return {
                                      ...tab,
                                      disabled: isDisabled,
                                      className: isDisabled ? classes.disabledTab : "",
                                      info: !isDisabled ? getTrialText(tab.url, modelDetails) : "",
                                  };
                              })
                    }
                    onChange={handleTabChange}
                >
                    <RoomByRoom modelSave={handleSave} />
                    <EnergyPlus modelSave={handleSave} validateH2kExport={() => {}} modelId={modelId} />
                    <UtilityAnalysis
                        accessor={`model.modelData.utilityData`}
                        history={history}
                        change={handleChange}
                        modelSave={handleSave}
                    />
                    <HybridHeating modelId={modelId} modelSave={handleSave} />
                    <SingleZoneCooling modelSave={handleSave} />
                </VerticalTabs>

                <DrawingNew
                    open={drawingOpen}
                    toggleOpen={setDrawingOpen}
                    modelChange={handleChange}
                    modelSave={handleSave}
                    modelName={name}
                    saving={submitting}
                />
                <ImageGallery handleSave={handleSave} isSavingModel={submitting} />
                <Settings change={handleChange} open={settingsOpen} toggleOpen={setSettingsOpen} />
                <VersionControlDrawer isOpen={versionHistoryOpen} close={() => setVersionHistoryOpen(false)} />
            </div>
            <ActivateInsightsModal
                open={!hasAccess && isActivateInsightsModalOpen}
                onClose={() => setIsActivateInsightsModalOpen(false)}
                modelId={modelId}
                modelName={name}
            />
        </>
    );
};

export default Insights;
